import { useState, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/features/auth/AuthProvider'
import type { Compra, Producto } from '@/types/database'
import { useProductos, useDepositos, getPrecio } from '@/features/productos/hooks'
import {
  useProveedores, useCompras, useCrearProveedor, useCrearCompra, useRecibirCompra, useAnularCompra,
  type CompraItemInput,
} from './hooks'
import { PlusIcon, CloseIcon, TrashIcon, SearchIcon, CheckIcon } from '@/components/shared/Icons'

const fmt = (n: number) =>
  new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

const fmtFecha = (iso: string) =>
  new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })

const ESTADO_CONFIG = {
  pendiente: { label: 'Pendiente', pill: 'me-pill-warn' },
  recibida:  { label: 'Recibida',  pill: 'me-pill-ok' },
  anulada:   { label: 'Anulada',   pill: 'me-pill-neutral' },
} as const

interface CartItem {
  producto: Producto
  presentacionId: string | null
  cantidad: number
  precioCosto: number
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ComprasPage() {
  const { user } = useAuth()
  const [drawerOpen, setDrawerOpen]       = useState(false)
  const [detalle, setDetalle]             = useState<Compra | null>(null)
  const [filtroEstado, setFiltroEstado]   = useState<string>('all')

  const filtros = filtroEstado === 'all' ? {} : { estado: filtroEstado as 'pendiente' | 'recibida' | 'anulada' }
  const { data: compras = [], isLoading } = useCompras(filtros)
  const pendientes = compras.filter(c => c.estado === 'pendiente').length

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 4 }}>Inventario</div>
          <h1 className="me-page-title">Compras</h1>
        </div>
        <button className="me-btn me-btn-primary" onClick={() => setDrawerOpen(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <PlusIcon size={14} strokeWidth={2} /> Nueva compra
        </button>
      </div>

      {/* Estado tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {(['all', 'pendiente', 'recibida', 'anulada'] as const).map(e => {
          const active = filtroEstado === e
          return (
            <button
              key={e}
              onClick={() => setFiltroEstado(e)}
              style={{
                padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: active ? 'var(--me-text)' : 'var(--me-surface)',
                color: active ? 'var(--me-bg)' : 'var(--me-text-2)',
                fontFamily: 'var(--me-sans)', fontSize: 13, fontWeight: active ? 600 : 400,
                transition: 'background var(--me-dur-short), color var(--me-dur-short)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {e === 'all' ? 'Todas' : ESTADO_CONFIG[e].label}
              {e === 'pendiente' && pendientes > 0 && (
                <span style={{
                  background: 'var(--me-warn)', color: '#fff', borderRadius: '50%',
                  width: 18, height: 18, display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 10, fontWeight: 700,
                }}>
                  {pendientes}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Table */}
      <div className="me-card" style={{ padding: 0, overflow: 'clip' }}>
        {isLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--me-text-3)' }}>Cargando...</div>
        ) : compras.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--me-text-3)', fontSize: 13.5 }}>
            {filtroEstado === 'all'
              ? 'Sin compras registradas — creá la primera con el botón de arriba'
              : `Sin compras con estado "${ESTADO_CONFIG[filtroEstado as keyof typeof ESTADO_CONFIG]?.label ?? filtroEstado}"`}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="me-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Proveedor</th>
                  <th>Estado</th>
                  <th style={{ textAlign: 'right' }}>Artículos</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {compras.map((c, i) => (
                  <tr
                    key={c.id}
                    style={{ background: i % 2 === 0 ? 'transparent' : 'var(--me-surface-2)', cursor: 'pointer' }}
                    onClick={() => setDetalle(c)}
                  >
                    <td style={{ fontFamily: 'var(--me-mono)', fontSize: 12, color: 'var(--me-text-3)' }}>
                      #{String(c.numero).padStart(5, '0')}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--me-text-2)' }}>{fmtFecha(c.created_at)}</td>
                    <td style={{ fontWeight: 500 }}>
                      {(c as unknown as { proveedores?: { nombre: string } }).proveedores?.nombre ?? '—'}
                    </td>
                    <td>
                      <span className={`me-pill ${ESTADO_CONFIG[c.estado].pill}`}>
                        {ESTADO_CONFIG[c.estado].label}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13, color: 'var(--me-text-3)' }}>
                      {c.compra_items?.length ?? 0}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontWeight: 500 }}>
                      ${fmt(c.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {drawerOpen && (
        <NuevaCompraDrawer usuarioId={user?.id ?? ''} onClose={() => setDrawerOpen(false)} />
      )}

      {detalle && (
        <CompraDetalleModal
          compra={detalle}
          usuarioId={user?.id ?? ''}
          onClose={() => setDetalle(null)}
        />
      )}
    </div>
  )
}

// ─── Nueva compra drawer ──────────────────────────────────────────────────────

function NuevaCompraDrawer({ usuarioId, onClose }: { usuarioId: string; onClose: () => void }) {
  const { data: proveedores = [] } = useProveedores()
  const { data: productos = [] }   = useProductos()
  const crear                      = useCrearCompra()
  const crearProveedor             = useCrearProveedor()

  const [proveedorId, setProveedorId]         = useState('')
  const [items, setItems]                     = useState<CartItem[]>([])
  const [notas, setNotas]                     = useState('')
  const [busqueda, setBusqueda]               = useState('')
  const [showNuevoProv, setShowNuevoProv]     = useState(false)
  const [nuevoProv, setNuevoProv]             = useState({ nombre: '', telefono: '' })

  const total = items.reduce((s, i) => s + i.cantidad * i.precioCosto, 0)

  const resultados = useMemo(() => {
    if (!busqueda.trim()) return []
    const q = busqueda.toLowerCase()
    return productos
      .filter(p => p.nombre.toLowerCase().includes(q) || (p.marca ?? '').toLowerCase().includes(q) || (p.codigo_barras ?? '').includes(q))
      .slice(0, 6)
  }, [busqueda, productos])

  function addItem(p: Producto) {
    const presentacionId = p.producto_presentaciones?.[0]?.id ?? null
    const precioCosto = getPrecio(p, 'costo')
    setBusqueda('')
    setItems(prev => {
      const ex = prev.find(i => i.producto.id === p.id)
      if (ex) return prev.map(i => i.producto.id === p.id ? { ...i, cantidad: i.cantidad + 1 } : i)
      return [...prev, { producto: p, presentacionId, cantidad: 1, precioCosto }]
    })
  }

  function setItemField(id: string, field: 'cantidad' | 'precioCosto', val: number) {
    setItems(prev => prev.map(i => i.producto.id === id ? { ...i, [field]: val } : i))
  }

  function removeItem(id: string) {
    setItems(prev => prev.filter(i => i.producto.id !== id))
  }

  async function handleCrearProveedor() {
    if (!nuevoProv.nombre.trim()) return
    try {
      const p = await crearProveedor.mutateAsync(nuevoProv)
      setProveedorId(p.id)
      setShowNuevoProv(false)
      setNuevoProv({ nombre: '', telefono: '' })
      toast.success('Proveedor creado')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error')
    }
  }

  async function handleSubmit() {
    if (!proveedorId) { toast.error('Seleccioná un proveedor'); return }
    if (items.length === 0) { toast.error('Agregá al menos un artículo'); return }
    try {
      const payload: CompraItemInput[] = items.map(i => ({
        productoId: i.producto.id, presentacionId: i.presentacionId,
        cantidad: i.cantidad, precioCosto: i.precioCosto,
      }))
      const { numero } = await crear.mutateAsync({ proveedorId, items: payload, notas, usuarioId })
      toast.success(`Compra #${String(numero).padStart(5, '0')} creada`)
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al crear compra')
    }
  }

  const totalUnidades = items.reduce((s, i) => s + i.cantidad, 0)

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 38, background: 'rgba(0,0,0,0.28)' }} />
      <div className="me-drawer-in" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 45,
        width: 520, background: 'var(--me-surface)',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.14)',
        display: 'flex', flexDirection: 'column',
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid var(--me-line)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0, background: 'var(--me-surface)',
        }}>
          <div>
            <div className="me-eyebrow" style={{ marginBottom: 2 }}>Inventario</div>
            <h2 className="me-section-title" style={{ margin: 0 }}>Nueva compra</h2>
          </div>
          <button className="me-icon-btn" onClick={onClose} title="Cerrar">
            <CloseIcon size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Proveedor */}
          <section>
            <DrawerLabel>Proveedor</DrawerLabel>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                className="me-input"
                style={{ flex: 1 }}
                value={proveedorId}
                onChange={e => setProveedorId(e.target.value)}
              >
                <option value="">Seleccioná un proveedor…</option>
                {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setShowNuevoProv(v => !v)}
                style={{
                  flexShrink: 0, padding: '0 12px', height: 36, borderRadius: 8,
                  border: '1px solid var(--me-line-2)', background: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, color: 'var(--me-text-3)',
                  display: 'flex', alignItems: 'center', gap: 4,
                  transition: 'color var(--me-dur-short), border-color var(--me-dur-short)',
                }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--me-text)'; e.currentTarget.style.borderColor = 'var(--me-line-3)' }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--me-text-3)'; e.currentTarget.style.borderColor = 'var(--me-line-2)' }}
              >
                <PlusIcon size={12} strokeWidth={2.5} />
                Nuevo
              </button>
            </div>

            {showNuevoProv && (
              <div style={{
                marginTop: 10, padding: 14, borderRadius: 10,
                background: 'var(--me-surface-2)', border: '1px solid var(--me-line)',
                display: 'flex', flexDirection: 'column', gap: 8,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--me-text-3)', marginBottom: 2 }}>
                  Nuevo proveedor
                </div>
                <input
                  className="me-input" style={{ width: '100%' }}
                  placeholder="Nombre *" value={nuevoProv.nombre}
                  onChange={e => setNuevoProv(p => ({ ...p, nombre: e.target.value }))}
                  autoFocus
                />
                <input
                  className="me-input" style={{ width: '100%' }}
                  placeholder="Teléfono (opcional)" value={nuevoProv.telefono}
                  onChange={e => setNuevoProv(p => ({ ...p, telefono: e.target.value }))}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                  <button className="me-btn me-btn-ghost" style={{ flex: 1 }} onClick={() => setShowNuevoProv(false)}>
                    Cancelar
                  </button>
                  <button
                    className="me-btn me-btn-primary" style={{ flex: 1 }}
                    onClick={handleCrearProveedor}
                    disabled={crearProveedor.isPending || !nuevoProv.nombre.trim()}
                  >
                    {crearProveedor.isPending ? 'Creando…' : 'Crear proveedor'}
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Buscar artículos */}
          <section>
            <DrawerLabel>Artículos</DrawerLabel>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--me-text-3)', pointerEvents: 'none', display: 'flex',
              }}>
                <SearchIcon size={14} strokeWidth={1.8} />
              </span>
              <input
                className="me-input"
                style={{ width: '100%', paddingLeft: 34 }}
                placeholder="Buscar por nombre, marca o código de barras…"
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                autoComplete="off"
              />
              {/* Floating results */}
              {resultados.length > 0 && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 10,
                  borderRadius: 10, border: '1px solid var(--me-line-2)',
                  background: 'var(--me-surface)', boxShadow: 'var(--me-shadow-popover)',
                  overflow: 'hidden',
                }}>
                  {resultados.map((p, idx) => {
                    const precioCosto = getPrecio(p, 'costo')
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => addItem(p)}
                        style={{
                          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
                          borderBottom: idx < resultados.length - 1 ? '1px solid var(--me-line-2)' : 'none',
                          textAlign: 'left', transition: 'background var(--me-dur-micro)',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--me-surface-2)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                      >
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>{p.nombre}</div>
                          {p.marca && <div style={{ fontSize: 11, color: 'var(--me-text-3)', marginTop: 1 }}>{p.marca}</div>}
                        </div>
                        {precioCosto > 0 && (
                          <span style={{ fontFamily: 'var(--me-mono)', fontSize: 12, color: 'var(--me-text-3)', flexShrink: 0, marginLeft: 12 }}>
                            ${fmt(precioCosto)}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Items table */}
            {items.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {/* Column headers */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 76px 96px 60px 28px',
                  gap: 6, padding: '0 4px 6px',
                  borderBottom: '1px solid var(--me-line)',
                }}>
                  {['Producto', 'Cant.', 'P. costo', 'Subtotal', ''].map((h, i) => (
                    <div key={i} style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
                      textTransform: 'uppercase', color: 'var(--me-text-3)',
                      textAlign: i >= 1 && i <= 3 ? 'center' : 'left',
                    }}>{h}</div>
                  ))}
                </div>
                {/* Rows */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {items.map((item, idx) => (
                    <div key={item.producto.id} style={{
                      display: 'grid', gridTemplateColumns: '1fr 76px 96px 60px 28px',
                      gap: 6, alignItems: 'center', padding: '8px 4px',
                      borderBottom: idx < items.length - 1 ? '1px solid var(--me-line-2)' : 'none',
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.producto.nombre}
                        </div>
                        {item.producto.marca && (
                          <div style={{ fontSize: 11, color: 'var(--me-text-3)' }}>{item.producto.marca}</div>
                        )}
                      </div>
                      <input
                        className="me-input"
                        style={{ fontFamily: 'var(--me-mono)', textAlign: 'center', padding: '5px 4px', fontSize: 13, width: '100%' }}
                        type="number" min="1" step="1"
                        value={item.cantidad}
                        onChange={e => setItemField(item.producto.id, 'cantidad', Math.max(1, parseInt(e.target.value) || 1))}
                      />
                      <div style={{ position: 'relative' }}>
                        <span style={{
                          position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)',
                          color: 'var(--me-text-3)', fontSize: 11, pointerEvents: 'none',
                        }}>$</span>
                        <input
                          className="me-input"
                          style={{ fontFamily: 'var(--me-mono)', paddingLeft: 14, padding: '5px 4px 5px 14px', fontSize: 12, width: '100%' }}
                          type="number" min="0" step="0.01"
                          value={item.precioCosto === 0 ? '' : item.precioCosto}
                          onChange={e => setItemField(item.producto.id, 'precioCosto', parseFloat(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                      <div style={{
                        fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                        fontSize: 12, fontWeight: 500, textAlign: 'right', color: 'var(--me-text-2)',
                      }}>
                        ${fmt(item.cantidad * item.precioCosto)}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.producto.id)}
                        style={{
                          width: 24, height: 24, borderRadius: 6, border: 'none',
                          background: 'none', cursor: 'pointer', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          color: 'var(--me-text-4)',
                          transition: 'color var(--me-dur-short), background var(--me-dur-short)',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--me-bad)'; e.currentTarget.style.background = 'var(--me-bad-soft)' }}
                        onMouseLeave={e => { e.currentTarget.style.color = 'var(--me-text-4)'; e.currentTarget.style.background = 'none' }}
                      >
                        <TrashIcon size={13} strokeWidth={1.8} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {items.length === 0 && (
              <div style={{
                marginTop: 8, padding: '20px 16px', borderRadius: 10,
                background: 'var(--me-surface-2)', border: '1px dashed var(--me-line-2)',
                textAlign: 'center', fontSize: 13, color: 'var(--me-text-3)',
              }}>
                Buscá productos para agregarlos
              </div>
            )}
          </section>

          {/* Notas */}
          <section>
            <DrawerLabel optional>Notas</DrawerLabel>
            <textarea
              className="me-input"
              style={{ width: '100%', minHeight: 64, resize: 'vertical', lineHeight: 1.5, fontSize: 13 }}
              value={notas}
              onChange={e => setNotas(e.target.value)}
              placeholder="Nro. de factura, condiciones, observaciones…"
            />
          </section>
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: '14px 24px 18px', borderTop: '1px solid var(--me-line)',
          flexShrink: 0, background: 'var(--me-surface)',
        }}>
          {items.length > 0 && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              marginBottom: 12, padding: '10px 14px',
              borderRadius: 10, background: 'var(--me-surface-2)',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 11, color: 'var(--me-text-3)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  Total compra
                </span>
                <span style={{ fontSize: 12, color: 'var(--me-text-3)' }}>
                  {items.length} {items.length === 1 ? 'artículo' : 'artículos'} · {totalUnidades} {totalUnidades === 1 ? 'unidad' : 'unidades'}
                </span>
              </div>
              <span style={{
                fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--me-text)',
              }}>
                ${fmt(total)}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="me-btn me-btn-ghost" onClick={onClose} style={{ flex: 1 }}>
              Cancelar
            </button>
            <button
              className="me-btn me-btn-primary"
              onClick={handleSubmit}
              disabled={crear.isPending}
              style={{ flex: 2 }}
            >
              {crear.isPending ? 'Creando…' : 'Crear compra'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// ─── Detalle modal ─────────────────────────────────────────────────────────────

function CompraDetalleModal({ compra, usuarioId, onClose }: { compra: Compra; usuarioId: string; onClose: () => void }) {
  const { data: depositos = [] } = useDepositos()
  const recibir = useRecibirCompra()
  const anular  = useAnularCompra()

  const depositoId = depositos[0]?.id ?? ''
  const isPendiente = compra.estado === 'pendiente'
  const prov = (compra as unknown as { proveedores?: { nombre: string } }).proveedores?.nombre ?? '—'

  async function handleRecibir() {
    if (!depositoId) { toast.error('No hay depósito configurado'); return }
    try {
      await recibir.mutateAsync({
        compraId: compra.id,
        items: (compra.compra_items ?? []).map(i => ({ productoId: i.producto_id, cantidad: i.cantidad })),
        depositoId,
        usuarioId,
      })
      toast.success('Compra recibida — stock actualizado')
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al recibir')
    }
  }

  async function handleAnular() {
    try {
      await anular.mutateAsync(compra.id)
      toast.success('Compra anulada')
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al anular')
    }
  }

  return (
    <>
      <div className="me-overlay me-overlay-in" onClick={onClose} />
      <div className="me-modal me-modal-in" style={{ maxWidth: 560 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--me-line)' }}>
          <div>
            <h2 className="me-section-title">
              Compra #{String(compra.numero).padStart(5, '0')}
            </h2>
            <div style={{ fontSize: 12, color: 'var(--me-text-3)', marginTop: 2 }}>
              {prov} · {fmtFecha(compra.created_at)}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className={`me-pill ${ESTADO_CONFIG[compra.estado].pill}`}>
              {ESTADO_CONFIG[compra.estado].label}
            </span>
            <button className="me-icon-btn" onClick={onClose}><CloseIcon size={18} /></button>
          </div>
        </div>

        {/* Items */}
        <div style={{ padding: '0', overflowX: 'auto' }}>
          <table className="me-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Producto</th>
                <th style={{ textAlign: 'right' }}>Cant.</th>
                <th style={{ textAlign: 'right' }}>P. costo</th>
                <th style={{ textAlign: 'right' }}>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {(compra.compra_items ?? []).map((item, i) => {
                const prod = (item as unknown as { productos?: { nombre: string; marca?: string } }).productos
                return (
                  <tr key={item.id} style={{ background: i % 2 === 0 ? 'transparent' : 'var(--me-surface-2)' }}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{prod?.nombre ?? '—'}</div>
                      {prod?.marca && <div style={{ fontSize: 12, color: 'var(--me-text-3)' }}>{prod.marca}</div>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontSize: 13 }}>{item.cantidad}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13 }}>
                      ${fmt(item.precio_costo)}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13, fontWeight: 500 }}>
                      ${fmt(item.subtotal)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Total row */}
        <div style={{ padding: '12px 24px 0', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--me-line-2)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span style={{ fontSize: 13, color: 'var(--me-text-3)' }}>Total</span>
            <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 20, fontWeight: 600 }}>
              ${fmt(compra.total)}
            </span>
          </div>
        </div>

        {/* Notas */}
        {compra.notas && (
          <div style={{ padding: '12px 24px 0' }}>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--me-text-3)', marginBottom: 4 }}>Notas</div>
            <div style={{ fontSize: 13, color: 'var(--me-text-2)' }}>{compra.notas}</div>
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, padding: '16px 24px', borderTop: '1px solid var(--me-line)', marginTop: 16 }}>
          {isPendiente && (
            <button className="me-btn me-btn-danger" onClick={handleAnular} disabled={anular.isPending}>
              Anular
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="me-btn me-btn-ghost" onClick={onClose}>Cerrar</button>
          {isPendiente && (
            <button
              className="me-btn me-btn-primary"
              onClick={handleRecibir}
              disabled={recibir.isPending}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <CheckIcon size={14} strokeWidth={2} />
              {recibir.isPending ? 'Recibiendo...' : 'Recibir compra'}
            </button>
          )}
        </div>
      </div>
    </>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function DrawerLabel({ children, optional }: { children: React.ReactNode; optional?: boolean }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
      color: 'var(--me-text-3)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6,
    }}>
      {children}
      {optional && (
        <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11, color: 'var(--me-text-4)' }}>
          (opcional)
        </span>
      )}
    </div>
  )
}
